/**
 * App Component
 *
 * Main application component with routing and theme provider.
 */

import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useThemeStore } from '@/store/themeStore';
import { useSocketStore } from '@/store/socketStore';
import Layout from '@/components/layout/Layout';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AdminRoute from '@/components/auth/AdminRoute';

// Lazy load pages for code splitting
const Login = lazy(() => import('@/pages/Login'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Profile = lazy(() => import('@/pages/Profile'));
const ContentList = lazy(() => import('@/pages/ContentList'));
const ContentDetail = lazy(() => import('@/pages/ContentDetail'));
const ContentEditor = lazy(() => import('@/pages/ContentEditor'));
const UserSales = lazy(() => import('@/pages/UserSales'));
const Admin = lazy(() => import('@/pages/Admin'));
const AdminUsers = lazy(() => import('@/pages/AdminUsers'));
const AdminContent = lazy(() => import('@/pages/AdminContent'));
const AdminSales = lazy(() => import('@/pages/AdminSales'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

function App() {
  const { checkAuth, isLoading: authLoading } = useAuthStore();
  const { theme, initTheme } = useThemeStore();
  const { connect, disconnect } = useSocketStore();
  const { isAuthenticated, user } = useAuthStore();

  // Initialize theme on mount
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // Apply theme class to document and update iOS theme-color
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    // Update all theme-color meta tags for iOS status bar
    document.querySelectorAll('meta[name="theme-color"]').forEach((el) => {
      el.setAttribute('content', theme === 'dark' ? '#030d1a' : '#ffffff');
    });
  }, [theme]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Connect to socket when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, user, connect, disconnect]);

  // Show loading while checking auth
  if (authLoading) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected routes with layout */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/content" element={<ContentList />} />
          <Route path="/content/new" element={<ContentEditor />} />
          <Route path="/content/:id" element={<ContentDetail />} />
          <Route path="/content/:id/edit" element={<ContentEditor />} />
          <Route path="/sales" element={<UserSales />} />
        </Route>

        {/* Admin routes */}
        <Route element={<AdminRoute><Layout /></AdminRoute>}>
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/content" element={<AdminContent />} />
          <Route path="/admin/sales" element={<AdminSales />} />
        </Route>

        {/* 404 */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
